import React, { useEffect, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { useLocation, useNavigate } from 'react-router-dom';
import MapAddAddress from '../Maps/MapAddAddress';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Search from '../../assets/images/mirror-search-image.png';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { getUserProfile, UserProfile } from '../../API/UserProfileApi/GetUserProfile';
import { getNearbyPinCodesPamCodeAsync } from '../../API/Address/GetNearbyPinCodesPamCodeAsync';
import { addNewAddressAsync } from '../../API/Address/AddNewAddressAsync';
import { addAddressImagesAsync } from '../../API/Address/AddAddressImagesAsync';
import { useLocalization } from '../Context/MultiLanguageContext';
import ReactGA from 'react-ga4';
import { jwtDecode } from 'jwt-decode';

const AddNewAddress: React.FC<{ updateUserName: (newUserName: string) => void }> = ({
  updateUserName,
}) => {
  const navigate = useNavigate();
  const { t } = useLocalization();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        if (Number(decoded.RoleId) === 3) {
          toast.info('Please login to add address', { toastId: 'guest-login' });
          navigate('/', { replace: true });
        }
      } catch (e) { }
    }
  }, [navigate, t]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [componentKey, setComponentKey] = useState(0);
  const [userName, setUserName] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [addressType, setAddressType] = useState<string>('Home');
  const [isCustom, setIsCustom] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [searchedLocation, setSearchedLocation] = useState<{ lat: number; lng: number } | null>(
    null
  );

  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageError, setImageError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    pinCode: '',
    latitude: '',
    longitude: '',
    blockCode: '',
    notes: '',
    url: '',
  });

  interface Errors {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    pinCode: string;
    addressType: string;
    location: string;
  }

  interface PinCodeData {
    pinCode: string;
    blockCode: string;
  }

  const [errors, setErrors] = useState<Errors>({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    pinCode: '',
    addressType: '',
    location: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nearbyPinCodes, setNearbyPinCodes] = useState<PinCodeData[]>([]);
  const location = useLocation();

  const fetchPlaceSuggestions = async (query: string) => {
    const response = await fetch(
      `https://photon.komoot.io/api/?q=${encodeURIComponent(
        query.trim()
      )}&lat=10.8505&lon=76.2711&limit=5`
    );
    const result = await response.json();

    return (result.features || []).map((feature: any) => {
      const properties = feature.properties || {};
      const displayName = [
        properties.name,
        properties.street,
        properties.city || properties.county,
        properties.state,
        properties.country,
      ]
        .filter(Boolean)
        .filter((part: string, index: number, parts: string[]) =>
          parts.indexOf(part) === index
        )
        .join(', ');

      return {
        display_name: displayName,
        lat: feature.geometry?.coordinates?.[1],
        lon: feature.geometry?.coordinates?.[0],
      };
    });
  };

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: 'Add New Address Page',
    });
  }, [location.pathname, location.search]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim()) {
      fetchPlaceSuggestions(value)
        .then((data) => setSuggestions(data))
        .catch((error) => console.error('Error fetching suggestions:', error));
    } else {
      setSuggestions([]);
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    fetchPlaceSuggestions(searchQuery)
      .then((data) => {
        if (data.length > 0) {
          const { lat, lon, display_name } = data[0];
          setSearchQuery(display_name);
          setSearchedLocation({ lat: parseFloat(lat), lng: parseFloat(lon) });
          setSuggestions([]);
        } else {
          alert('Location not found');
        }
      })
      .catch((error) => console.error('Error fetching search results:', error));
  };

  const handleSuggestionClick = (lat: number, lon: number, displayName: string) => {
    setSearchQuery(displayName);
    setSearchedLocation({ lat, lng: lon });
    setSuggestions([]);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    setErrors((prev) => ({
      ...prev,
      [name as keyof Errors]: value ? '' : prev[name as keyof Errors],
    }));
  };

  const handleLocationChange = (
    lat: string,
    lon: string,
    pinCode: string,
    street: string,
    city: string
  ) => {
    setFormData((prevData) => ({
      ...prevData,
      latitude: lat,
      longitude: lon,
      pinCode: pinCode === 'N/A' ? '' : pinCode,
      addressLine1: '',
      addressLine2: street === 'N/A' ? '' : street,
      addressLine3: city === 'N/A' ? '' : city,
    }));

    setErrors((prev) => ({
      ...prev,
      location: '',
      pinCode: pinCode ? '' : prev.pinCode,
      addressLine1: '',
      addressLine2: street ? '' : prev.addressLine2,
      addressLine3: city ? '' : prev.addressLine3,
    }));

    ReactGA.event({
      category: 'Address',
      action: 'Location Selected',
      label: `Lat: ${lat}, Lon: ${lon}`,
    });
  };

  const handleAddressTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;

    if (selectedValue === 'custom') {
      setIsCustom(true);
      setAddressType('');
    } else {
      setIsCustom(false);
      setAddressType(selectedValue);
    }
  };

  const handleCustomAddressTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressType(e.target.value);
  };

  const handlePinCodeChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedPinCode = e.target.value;
    const selectedPinCodeData = nearbyPinCodes.find((pin) => pin.pinCode === selectedPinCode);

    if (selectedPinCodeData) {
      if (selectedPinCodeData.pinCode !== nearbyPinCodes[0].pinCode) {
        if (!toast.isActive('info')) {
          toast.info(t('recommendedChange'), { toastId: 'info' });
        }
      }

      setFormData({
        ...formData,
        pinCode: selectedPinCodeData.pinCode,
        blockCode: selectedPinCodeData.blockCode,
      });
    }
  };

  const fetchNearbyPinCodes = async (latitude: number, longitude: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t('sessionExpired'));
      return;
    }

    try {
      const formattedPinCodes = await getNearbyPinCodesPamCodeAsync(
        latitude,
        longitude,
        token,
        t
      );
      if (!formattedPinCodes) {
        return;
      }

      setNearbyPinCodes(formattedPinCodes);
      if (formattedPinCodes.length > 0) {
        setFormData((prevData) => ({
          ...prevData,
          pinCode: formattedPinCodes[0].pinCode,
          blockCode: formattedPinCodes[0].blockCode,
        }));
      }
    } catch (error) { }
  };

  useEffect(() => {
    if (formData.latitude && formData.longitude) {
      fetchNearbyPinCodes(Number(formData.latitude), Number(formData.longitude));
    }
  }, [formData.latitude, formData.longitude]);

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);

    setImageError('');

    if (files.length === 0) return;

    const remainingSlots = 3 - selectedImages.length;

    if (files.length > remainingSlots) {
      setImageError(
        `You can add only ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''
        }.`
      );
      e.target.value = '';
      return;
    }

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp',
    ];

    const invalidFile = files.find(
      (file) =>
        !allowedTypes.includes(file.type) ||
        file.size > 5 * 1024 * 1024
    );

    if (invalidFile) {
      if (!allowedTypes.includes(invalidFile.type)) {
        setImageError(
          'Only JPG, JPEG, PNG and WEBP images are allowed.'
        );
      } else {
        setImageError('Each image must be less than 5 MB.');
      }

      e.target.value = '';
      return;
    }

    // ADD to existing images
    setSelectedImages((prev) => [
      ...prev,
      ...files,
    ]);

    // ADD previews to existing previews
    const newPreviews = files.map((file) =>
      URL.createObjectURL(file)
    );

    setImagePreviews((prev) => [
      ...prev,
      ...newPreviews,
    ]);

    // Allow selecting the same file again
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setErrors({
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      pinCode: '',
      addressType: '',
      location: '',
    });

    let hasErrors = false;

    if (!formData.addressLine1) {
      setErrors((prev) => ({ ...prev, addressLine1: t('EnterBuilding') }));
      hasErrors = true;
    }
    if (!formData.addressLine2) {
      setErrors((prev) => ({ ...prev, addressLine2: t('EnterStreet') }));
      hasErrors = true;
    }
    if (!formData.addressLine3) {
      setErrors((prev) => ({ ...prev, addressLine3: t('cityrequired') }));
      hasErrors = true;
    }
    if (!formData.pinCode) {
      setErrors((prev) => ({ ...prev, pinCode: t('pincoderequired') }));
      hasErrors = true;
    } else if (formData.pinCode.length !== 6) {
      setErrors((prev) => ({ ...prev, pinCode: t('pincoderequired6Digit') }));
      hasErrors = true;
    }
    if (!addressType) {
      setErrors((prev) => ({ ...prev, addressType: t('addressTypeenter') }));
      hasErrors = true;
    }
    if (!formData.latitude || !formData.longitude) {
      setErrors((prev) => ({ ...prev, location: t('locationSelect') }));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error(t('sessionExpired'));
      navigate('/');
      return;
    }

    ReactGA.event({
      category: 'Address',
      action: 'Submit New Address',
      label: 'Address Form Submission',
    });

    try {
      const payload = {
        email: formData.email || null,
        phoneNumber: formData.phoneNumber || null,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        addressLine3: formData.addressLine3,
        pamCode: formData.pinCode + formData.blockCode,
        latitude: formData.latitude,
        longitude: formData.longitude,
        addressType: addressType,
        notes: formData.notes,
        url: formData.url,
      };

      const result = await addNewAddressAsync(payload, token, t);

      if (!result) {
        return;
      }

      if (result.isActualPincodeSelected === false) {
        toast.info(t('recommendedChange'));
      }

      // Upload images after address is successfully created
      if (selectedImages.length > 0) {
        await addAddressImagesAsync(
          result.id,
          selectedImages,
          token,
          t
        );
      }

      setFormData({
        email: '',
        phoneNumber: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        pinCode: '',
        latitude: '',
        longitude: '',
        blockCode: '',
        notes: '',
        url: '',
      });

      setSelectedImages([]);
      setImagePreviews([]);
      setImageError('');

      navigate(`/address-details/${result.id}`, {
        state: { address: result },
      });


    } catch (error) {
      if (navigator.onLine) {
        toast.error(t('pincodeNotSupported'), { toastId: 'address-error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const fetchAndHandleUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      setUserProfile(data);
      setUserName(data.userName);
      updateUserName(data.userName);
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      toast.error(t('sessionExpired'));
      navigate('/home');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    sessionStorage.clear();

    navigate('/', { replace: true });
  };

  useEffect(() => { }, [componentKey]);

  return (
    <div className="flex min-h-screen bg-slate-100">
      <div className="flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
        <Header
          userName={userName}
          fetchUserProfile={fetchAndHandleUserProfile}
          handleLogout={handleLogout}
        />

        <main className="flex-1">
          <div className="container mx-auto px-4 flex flex-col flex-grow">
            <div className="bg-indigo-100 p-6 rounded-md shadow-sm relative mt-6 select-none w-full">
              <div className="flex flex-col lg:flex-row lg:space-x-4 justify-end items-end">
                <div className="relative rounded-md flex w-full lg:w-auto">
                  <input
                    type="text"
                    maxLength={60}
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder={t('SearchLocation')}
                    className="h-12 pl-4 pr-3 rounded-l-md focus:outline-none border-none placeholder-gray-500 w-full sm:w-[320px]"
                  />

                  <button
                    type="button"
                    onClick={handleSearch}
                    className="h-12 px-5 bg-blue-600 text-white text-sm rounded-r-md flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {suggestions.length > 0 && (
                    <ul className="absolute top-14 bg-white border rounded-lg shadow-lg w-full z-[2000] max-h-60 overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <li
                          key={index}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                          onClick={() =>
                            handleSuggestionClick(
                              parseFloat(suggestion.lat),
                              parseFloat(suggestion.lon),
                              suggestion.display_name
                            )
                          }
                        >
                          {suggestion.display_name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="hidden lg:block absolute left-4 bottom-0">
                <img src={Search} alt="Illustration" className="h-20" />
              </div>
            </div>

            <div className="container px-4 py-4 mx-auto bg-white m-4 rounded-lg">
              {successMessage && (
                <div
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4"
                  role="alert"
                >
                  <strong className="font-bold">Success!</strong>
                  <span className="block sm:inline ml-2">{successMessage}</span>
                </div>
              )}

              <div className="relative w-full mt-4">
                <MapAddAddress
                  key={componentKey}
                  onLocationChange={handleLocationChange}
                  searchedLocation={searchedLocation}
                />
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="pinCode" className="block text-sm font-medium text-gray-700">
                    {t('postalCode')} <span className="text-red-500">*</span>
                  </label>

                  <select
                    id="pinCodeDropdown"
                    name="pinCodeDropdown"
                    value={formData.pinCode}
                    onChange={handlePinCodeChangeSelect}
                    className={`w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none ${errors.pinCode ? 'border-red-500' : ''
                      }`}
                  >
                    {nearbyPinCodes.length > 0 && (
                      <option value={nearbyPinCodes[0].pinCode}>
                        {nearbyPinCodes[0].pinCode} (Recommended)
                      </option>
                    )}
                    {nearbyPinCodes.slice(1).map((pin, index) => (
                      <option key={index + 1} value={pin.pinCode}>
                        {pin.pinCode}
                      </option>
                    ))}
                  </select>

                  {errors.pinCode && (
                    <p className="mt-1 text-sm text-red-500">{errors.pinCode}</p>
                  )}
                  <ToastContainer />
                </div>

                <div>
                  <label
                    htmlFor="addressLine1"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('buildingNumber')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('EnterBuilding')}
                    maxLength={32}
                    id="addressLine1"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none ${errors.addressLine1 ? 'border-red-500' : ''
                      }`}
                  />
                  {errors.addressLine1 && (
                    <p className="mt-1 text-sm text-red-500">{errors.addressLine1}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="addressLine2"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('street')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('EnterStreet')}
                    maxLength={32}
                    id="addressLine2"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none ${errors.addressLine2 ? 'border-red-500' : ''
                      }`}
                  />
                  {errors.addressLine2 && (
                    <p className="mt-1 text-sm text-red-500">{errors.addressLine2}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="addressLine3"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t('city')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter City"
                    maxLength={32}
                    id="addressLine3"
                    name="addressLine3"
                    value={formData.addressLine3}
                    onChange={handleInputChange}
                    className={`w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none ${errors.addressLine3 ? 'border-red-500' : ''
                      }`}
                  />
                  {errors.addressLine3 && (
                    <p className="mt-1 text-sm text-red-500">{errors.addressLine3}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    {t('addressType')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="address"
                    name="address"
                    className={`w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none ${errors.addressType ? 'border-red-500' : ''
                      }`}
                    value={isCustom ? 'custom' : addressType}
                    onChange={handleAddressTypeChange}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    <option value="home">{t('home')}</option>
                    <option value="office"> {t('office')}</option>
                    <option value="custom">{t('otherType')}</option>
                  </select>

                  {isCustom && (
                    <input
                      type="text"
                      className={`mt-2 w-full rounded-lg bg-slate-50 py-2 pl-6 outline-none focus:border-blue focus-visible:shadow-none ${errors.addressType ? 'border-red-500' : ''
                        }`}
                      placeholder="Add Your Own Address Type"
                      maxLength={15}
                      value={addressType}
                      onChange={handleCustomAddressTypeChange}
                      style={{ position: 'relative', zIndex: 1 }}
                    />
                  )}
                  {errors.addressType && (
                    <p className="mt-1 text-sm text-red-500">{errors.addressType}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                    URL <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="url"
                    id="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    maxLength={500}
                    className="w-full rounded-lg bg-slate-50 py-3 pl-6 pr-10 outline-none focus:border-blue focus-visible:shadow-none"
                  />
                </div>

                {/* Add Images */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Add Images
                      <span className="ml-1 text-gray-400 font-normal">(Optional)</span>
                    </label>

                    <span className="text-xs text-gray-400">
                      {selectedImages.length}/3
                    </span>
                  </div>

                  <div className="border border-gray-200 rounded-xl bg-gray-50 p-3">
                    <div className="flex flex-wrap gap-3">

                      {/* Image previews */}
                      {imagePreviews.map((preview, index) => (
                        <div
                          key={index}
                          className="relative w-28 h-24 rounded-lg overflow-hidden border border-gray-200 bg-white group"
                        >
                          <img
                            src={preview}
                            alt={`Address ${index + 1}`}
                            className="w-full h-full object-cover"
                          />

                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = selectedImages.filter(
                                (_, imageIndex) => imageIndex !== index
                              );

                              const newPreviews = imagePreviews.filter(
                                (_, imageIndex) => imageIndex !== index
                              );

                              setSelectedImages(newImages);
                              setImagePreviews(newPreviews);
                            }}
                            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full
                       bg-black/60 text-white text-sm
                       flex items-center justify-center
                       opacity-0 group-hover:opacity-100
                       transition-opacity hover:bg-red-500"
                            aria-label="Remove image"
                          >
                            ×
                          </button>

                          {/* Image number */}
                          <div className="absolute bottom-1 left-1 bg-black/50 text-white
                          text-[10px] px-1.5 py-0.5 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}

                      {/* Upload box */}
                      {selectedImages.length < 3 && (
                        <label
                          htmlFor="addressImages"
                          className="w-28 h-24 rounded-lg border-2 border-dashed
                     border-gray-300 bg-white
                     flex flex-col items-center justify-center
                     cursor-pointer
                     hover:border-blue-500 hover:bg-blue-50
                     transition-all"
                        >
                          <div className="w-9 h-9 rounded-full bg-blue-50
                          flex items-center justify-center mb-1">
                            <span className="text-xl text-blue-500">+</span>
                          </div>

                          <span className="text-xs font-medium text-gray-600">
                            Add Image
                          </span>
                        </label>
                      )}

                      <input
                        id="addressImages"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </div>

                    {/* Upload information */}
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                      <span>📷</span>
                      <span>
                        JPG, PNG or WEBP · Max 5 MB each · Up to 3 images
                      </span>
                    </div>
                  </div>

                  {imageError && (
                    <p className="mt-1.5 text-sm text-red-500">
                      {imageError}
                    </p>
                  )}
                </div>

                {/* Notes + Save Address */}
                <div className="col-span-1 sm:col-span-2 lg:col-span-3">
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end">

                    {/* Notes */}
                    <div>
                      <label
                        htmlFor="notes"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Notes{" "}
                        <span className="text-gray-400 font-normal">
                          (Optional)
                        </span>
                      </label>

                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Add any useful details about this address"
                        maxLength={500}
                        rows={2}
                        className="w-full resize-none rounded-lg bg-slate-50 border border-transparent
                   py-2.5 px-4 text-sm
                   outline-none
                   focus:border-blue-500
                   focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    {/* Save Address */}
                    <div className="flex lg:pb-0 lg:-translate-y-2">
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex items-center justify-center
                   w-full lg:w-auto min-w-[180px]
                   h-[42px]
                   px-5
                   bg-[#3461FD]
                   hover:bg-blue-700
                   text-white
                   rounded-md
                   shadow-sm
                   hover:shadow-md
                   transition-all duration-200
                   gap-2
                   disabled:cursor-not-allowed
                   disabled:opacity-60"
                      >
                        <span className="text-lg">
                          {isSubmitting ? (
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                          ) : (
                            "+"
                          )}
                        </span>

                        <span className="text-sm font-medium whitespace-nowrap">
                          {isSubmitting
                            ? `${t("saving")}...`
                            : t("saveThisAddress")}
                        </span>
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

const AddNewAddressSkeleton = () => (
  <div className="w-full p-6 bg-white rounded-lg shadow-md animate-pulse">
    <div className="h-[400px] bg-gray-200 rounded-lg mb-6"></div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>

      <div>
        <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>

      <div>
        <div className="h-4 w-16 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>

      <div>
        <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>

      <div>
        <div className="h-4 w-40 bg-gray-200 rounded mb-2"></div>
        <div className="h-10 w-full bg-gray-200 rounded"></div>
      </div>
    </div>

    <div className="flex justify-end mt-6">
      <div className="h-10 w-40 bg-gray-200 rounded"></div>
    </div>
  </div>
);

export default AddNewAddress;